<template>
  <a-drawer :title="title" width="min(960px, 100vw)" :visible="visible" :closable="!confirmLoading" :wrapClassName="'manual-check-drawer' + ($store.state.bgLight ? ' manual-check-light' : '')" @close="close">
    <div class="check-scroll" ref="scrollArea">
    <a-spin :spinning="confirmLoading || detailLoading">
      <div class="check-overview">
        <div class="site-heading"><a-icon type="environment" /><h2 :title="model.siteName">{{ model.siteName || '正在读取站点…' }}</h2></div>
        <div class="overview-actions">
          <a-radio-group v-if="!disableSubmit && !model.id" v-model="entryMode" button-style="solid" size="small">
            <a-radio-button :value="STAGE.SAMPLE">分阶段采样</a-radio-button>
            <a-radio-button :value="STAGE.COMPLETE">一次完整录入</a-radio-button>
          </a-radio-group>
        <span class="flow-badge" :class="{finished:completed}">{{ flowLabel }}</span>
        </div>
      </div>
      <a-alert v-if="legacy" message="历史完成记录保留原数据，到样信息无需补填。" type="info" show-icon class="legacy-notice" />
      <nav class="stage-nav" aria-label="检测阶段">
        <button v-for="(item,index) in stages" :key="item.value" type="button" :class="{selected:focusedStage===item.value,done:stageDone(item.value)}" :disabled="!stageAvailable(item.value) || confirmLoading || detailLoading" :aria-current="focusedStage===item.value ? 'step' : null" @click="selectStage(item.value)">
          <span class="stage-number"><a-icon v-if="stageDone(item.value)" type="check" /><template v-else>{{index+1}}</template></span>
          <span class="stage-caption"><strong>{{item.label}}</strong><small>{{stageDescription(item.value)}}</small></span>
          <a-icon v-if="focusedStage===item.value" type="right" />
        </button>
      </nav>
      <div v-if="!showAll && model.id" class="previous-summary">
        <span>采样：{{formatDate(model.samplingDate)}} · {{userLabel('samplingUser')}}</span>
        <span v-if="model.arrivalDate">到样：{{formatDate(model.arrivalDate)}} · {{userLabel('arrivalUser')}}</span>
      </div>
      <a-form layout="vertical" class="check-form" :class="{'readonly-form':disableSubmit}">
        <a-alert v-if="submitError" :message="submitError" type="error" show-icon class="factor-notice" />
        <a-alert v-if="userError && !disableSubmit" message="人员加载失败" type="warning" show-icon class="factor-notice"><a-button slot="description" size="small" :loading="userLoading" @click="loadUsers">重新加载人员</a-button></a-alert>
        <section v-show="showSection(STAGE.SAMPLE)" ref="sampleSection" class="stage-card">
        <div class="section-heading"><div><h3><span>01</span>采样信息</h3><p v-if="!disableSubmit">记录样品来源、采样人员及现场照片</p></div><span class="section-state">{{editable(STAGE.SAMPLE) ? '填写采样' : '采样记录'}}</span></div>
        <a-row :gutter="20" class="fields-row">
          <a-col v-if="model.samplingPointSelectable" :xs="24" :sm="12">
            <a-form-item label="采样点" :required="editable(STAGE.SAMPLE) && model.samplingPointSelectable">
              <a-select v-if="model.samplingPointSelectable && editable(STAGE.SAMPLE)" v-model="model.samplingPoint" placeholder="请选择进水口或出水口" @change="changePoint">
                <a-select-option v-if="model.id && model.samplingPoint === SAMPLING_POINT.DEFAULT" :value="SAMPLING_POINT.DEFAULT">默认采样点（历史）</a-select-option>
                <a-select-option :value="SAMPLING_POINT.INLET">进水口</a-select-option><a-select-option :value="SAMPLING_POINT.OUTLET">出水口</a-select-option>
              </a-select>
              <div v-else class="field-value">{{POINT_LABELS[model.samplingPoint] || '—'}}</div>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12"><a-form-item label="采样人员" :required="editable(STAGE.SAMPLE)"><a-tree-select v-if="editable(STAGE.SAMPLE)" v-model="model.samplingUser" v-bind="userSelectProps" placeholder="请选择采样人员" /><div v-else class="field-value">{{userLabel('samplingUser')}}</div></a-form-item></a-col>
          <a-col :xs="24" :sm="12"><a-form-item label="采样时间" :required="editable(STAGE.SAMPLE)"><a-date-picker v-if="editable(STAGE.SAMPLE)" v-model="model.samplingDate" show-time format="YYYY-MM-DD HH:mm:ss" placeholder="请选择采样时间" /><div v-else class="field-value">{{formatDate(model.samplingDate)}}</div></a-form-item></a-col>
        </a-row>
        <a-form-item label="采样备注" class="remarks-field"><a-textarea v-if="editable(STAGE.SAMPLE)" v-model="model.remark" placeholder="选填，补充采样或样品说明" :rows="2" /><div v-else class="field-value" :class="{'empty-value':!model.remark}">{{model.remark || '未填写'}}</div></a-form-item>
        <a-form-item label="采样照片" class="attachment-field">
        <div class="attachment-box"><p v-if="!disableSubmit" class="field-hint">选填，支持 JPG、PNG 等图片</p>
        <iws-upload :key="'sampling'+sessionKey" ref="samplingUpload" :filesrc="ATTACHMENT_SOURCE.SAMPLING" :srcid="model.id" :disableSubmit="!editable(STAGE.SAMPLE)" :options="{btnText:'上传采样照片'}" accept=".png,.jpg,.jpeg,.svg" />
        <span v-if="disableSubmit && !(model.samplingFileList || []).length" class="field-hint">暂无采样照片</span>
        </div></a-form-item>
        </section>
        <section v-show="showSection(STAGE.ARRIVAL)" ref="arrivalSection" class="stage-card">
        <div class="section-heading"><div><h3><span>02</span>到样信息</h3><p v-if="!disableSubmit">确认样品送达时间及接收人员</p></div><span class="section-state">{{editable(STAGE.ARRIVAL) ? '填写到样' : '到样记录'}}</span></div>
        <a-row :gutter="20" class="fields-row">
          <a-col :xs="24" :sm="12"><a-form-item label="到样时间" :required="editable(STAGE.ARRIVAL) && !legacy"><a-date-picker v-if="editable(STAGE.ARRIVAL)" v-model="model.arrivalDate" show-time format="YYYY-MM-DD HH:mm:ss" placeholder="请选择到样时间" /><div v-else class="field-value">{{formatDate(model.arrivalDate)}}</div></a-form-item></a-col>
          <a-col :xs="24" :sm="12"><a-form-item label="到样人员" :required="editable(STAGE.ARRIVAL) && !legacy"><a-tree-select v-if="editable(STAGE.ARRIVAL)" v-model="model.arrivalUser" v-bind="userSelectProps" placeholder="请选择到样人员" /><div v-else class="field-value">{{userLabel('arrivalUser')}}</div></a-form-item></a-col>
        </a-row>
        <a-form-item label="到样备注" class="remarks-field"><a-textarea v-if="editable(STAGE.ARRIVAL)" v-model="model.arrivalRemark" placeholder="选填，补充送达、交接或样品情况" :rows="2" /><div v-else class="field-value" :class="{'empty-value':!model.arrivalRemark}">{{model.arrivalRemark || '未填写'}}</div></a-form-item>
        <a-form-item label="到样照片" class="attachment-field">
        <div class="attachment-box"><p v-if="!disableSubmit" class="field-hint">选填，记录样品交接情况</p>
        <iws-upload :key="'arrival'+sessionKey" ref="arrivalUpload" :filesrc="ATTACHMENT_SOURCE.ARRIVAL" :srcid="model.id" :disableSubmit="!editable(STAGE.ARRIVAL)" :options="{btnText:'上传到样照片'}" accept=".png,.jpg,.jpeg,.svg" />
        <span v-if="disableSubmit && !(model.arrivalFileList || []).length" class="field-hint">暂无到样照片</span>
        </div></a-form-item>
        </section>
        <section v-show="showSection(STAGE.COMPLETE)" ref="testingSection" class="stage-card">
        <div class="section-heading"><div><h3><span>03</span>检测信息</h3><p v-if="!disableSubmit">填写检测结果并上传检测报告或图片</p></div><span class="section-state">{{editable(STAGE.COMPLETE) ? '填写检测' : '检测记录'}}</span></div>
        <a-row :gutter="20" class="fields-row">
          <a-col :xs="24" :sm="12"><a-form-item label="检测时间" :required="editable(STAGE.COMPLETE)"><a-date-picker v-if="editable(STAGE.COMPLETE)" v-model="model.checkDate" show-time format="YYYY-MM-DD HH:mm:ss" placeholder="请选择检测时间" /><div v-else class="field-value">{{formatDate(model.checkDate)}}</div></a-form-item></a-col>
          <a-col :xs="24" :sm="12"><a-form-item label="检测方式" :required="editable(STAGE.COMPLETE)"><a-radio-group v-if="editable(STAGE.COMPLETE)" v-model="model.checkType" button-style="solid" class="method-switch"><a-radio-button :value="CHECK_TYPE.SELF"><a-icon type="check-circle" /> 自测</a-radio-button><a-radio-button :value="CHECK_TYPE.THIRD_PARTY"><a-icon type="check-circle" /> 第三方实验室</a-radio-button></a-radio-group><div v-else class="field-value">{{CHECK_TYPE_LABELS[model.checkType] || model.checkType_dictText || '—'}}</div></a-form-item></a-col>
          <a-col :xs="24" :sm="12"><a-form-item label="检测人员" :required="editable(STAGE.COMPLETE) && !legacy"><a-tree-select v-if="editable(STAGE.COMPLETE)" v-model="model.checkUser" v-bind="userSelectProps" placeholder="请选择检测人员" /><div v-else class="field-value">{{userLabel('checkUser')}}</div></a-form-item></a-col>
          <a-col :xs="24" :sm="12" v-if="model.checkType === CHECK_TYPE.THIRD_PARTY"><a-form-item label="实验室名称" :required="editable(STAGE.COMPLETE)"><a-select v-if="editable(STAGE.COMPLETE)" v-model="model.companyId" placeholder="请选择第三方实验室" show-search optionFilterProp="children"><a-select-option v-for="company in companyList" :key="company.id" :value="company.id">{{company.name}}</a-select-option></a-select><div v-else class="field-value">{{model.laboratoryName || model.companyName || '—'}}</div></a-form-item></a-col>
        </a-row>
        <div class="result-heading"><strong>检测数据 <span v-if="editable(STAGE.COMPLETE)" class="required-mark">*</span></strong><span>{{disableSubmit ? '共 '+filledFactorCount+' 项检测结果' : filledFactorCount+' / '+paramData.length+' 项已填写 · 至少填写 1 项，0 为有效值'}}</span></div>
        <a-alert v-if="factorError" :message="factorError" type="error" show-icon class="factor-notice" />
        <a-alert v-if="qualificationError" :message="qualificationError" type="warning" show-icon class="factor-notice"><a-button slot="description" size="small" @click="evaluateFactors">重新判定</a-button></a-alert>
        <a-table class="factor-table" :columns="paramColumns" :dataSource="paramData" :pagination="false" :loading="factorLoading" :scroll="{x:720}" :locale="{emptyText: model.samplingPointSelectable && !model.samplingPoint ? '请先选择采样点' : '该采样点暂无已配置的检测项目'}" rowKey="code" size="middle">
          <template slot="factorName" slot-scope="text"><span class="factor-name">{{text}}</span></template>
          <template slot="factorUnit" slot-scope="text"><span class="factor-unit">{{text || '—'}}</span></template>
          <template slot="value" slot-scope="text, row">
            <a-input-number v-if="editable(STAGE.COMPLETE)" :min="0" :step="0.000001" v-model="row.value" placeholder="请输入检测值" :aria-label="row.title+'检测值'" @change="queueQualification" />
            <span v-else class="result-number">{{text == null ? '—' : text}}</span>
          </template>
          <template slot="emissionLimit" slot-scope="text, row"><span :title="row.targetStandardName">{{text || '—'}}</span></template>
          <template slot="qualification" slot-scope="text, row"><span :class="['qualification-pill',qualificationTone(row)]">{{qualificationText(row)}}</span></template>
        </a-table>
        <a-form-item label="检测备注" class="testing-remarks remarks-field"><a-textarea v-if="editable(STAGE.COMPLETE)" v-model="model.testingRemark" placeholder="选填，补充检测过程或结果说明" :rows="2" /><div v-else class="field-value" :class="{'empty-value':!model.testingRemark}">{{model.testingRemark || '未填写'}}</div></a-form-item>
        <div v-if="model.standardName" class="standard-summary"><a-icon type="safety-certificate" /> 环境标准：{{model.standardName}}</div>
        <a-form-item label="检测附件" class="attachment-field testing-attachments">
          <div class="attachment-box"><p v-if="!disableSubmit" class="field-hint">选填，支持检测报告及图片</p>
          <iws-upload v-show="!disableSubmit || (model.testingFileList || []).length" :key="'testing'+sessionKey" ref="testingUpload" :filesrc="ATTACHMENT_SOURCE.TESTING" :srcid="model.id" :disableSubmit="!editable(STAGE.COMPLETE)" :options="{btnText:'上传检测附件'}" />
          <span v-if="disableSubmit && !(model.testingFileList || []).length" class="field-hint">暂无检测附件</span>
          </div>
        </a-form-item>
        </section>
        <div v-if="model.createTime" class="audit-summary"><span>创建：{{model.createBy_userName}} · {{model.createTime}}</span><span v-if="model.updateTime">最后修改：{{model.updateBy_userName}} · {{model.updateTime}}</span></div>
      </a-form>
    </a-spin>
    </div>
    <div class="check-footer">
      <p><a-icon type="info-circle" /> {{footerHint}}</p>
      <div><a-button :disabled="confirmLoading" @click="close">{{disableSubmit ? '关闭' : '取消'}}</a-button>
      <a-button v-if="!disableSubmit" type="primary" :loading="confirmLoading" :disabled="detailLoading || factorLoading || !networkStatus" @click="handleOk">{{submitLabel}}</a-button></div>
    </div>
  </a-drawer>
</template>

<script>
import { ModalFormMixin } from '@/mixins/ModalFormMixin'
import IwsUpload from '@comp/upload/IwsUpload'
import { getAction, httpAction } from '@/api/manage'
import { queryUserTreeList } from '@/api/api'
import moment from 'moment'

import * as manualCheckConfig from '@/utils/manualCheck'
const { POINT_LABELS, CHECK_TYPE_LABELS, STAGE, FLOW_STATUS, SAMPLING_POINT, CHECK_TYPE, FLOW_VERSION, hasSamplingPoints, syncsMonitoring, DATE_TIME_FORMAT, ATTACHMENT_SOURCE, STAGE_OPTIONS, FLOW_LABELS, SUBMIT_LABELS, QUALIFICATION_LABELS, STAGE_PATHS, getStageFields, hasValue, buildCp } = manualCheckConfig

export default {
  name: 'ManualCheckDrawer',
  mixins: [ModalFormMixin],
  components: { IwsUpload },
  props: ['pagePath'],
  /** 初始化页面录入与异步请求状态。 */
  data() {
    return {
      POINT_LABELS, CHECK_TYPE_LABELS, STAGE, FLOW_STATUS, SAMPLING_POINT, CHECK_TYPE, ATTACHMENT_SOURCE,
      title: '人工检测',
      entryMode: STAGE.SAMPLE, activeStage: STAGE.ARRIVAL, overviewStage: STAGE.SAMPLE, sessionKey: 0, detailLoading: false, submitError: '',
      stages: STAGE_OPTIONS,
      factorLoading: false, factorError: '', userTree: [], userLoading: false, userError: '', companyList: [], paramData: [],
      qualificationTimer: null, qualificationRequest: 0, qualificationLoading: false, qualificationError: '',
      paramColumns: [
        { title:'检测项目', dataIndex:'title', scopedSlots:{customRender:'factorName'} },
        { title:'单位', dataIndex:'unitName', width:80, scopedSlots:{customRender:'factorUnit'} },
        { title:'检测值', dataIndex:'value', width:210, scopedSlots:{customRender:'value'} },
        { title:'排放限值', dataIndex:'emissionLimit', width:130, scopedSlots:{customRender:'emissionLimit'} },
        { title:'是否合格', dataIndex:'qualificationStatus', width:120, scopedSlots:{customRender:'qualification'} }
      ]
    }
  },
  computed: {
    /** 复用现有人员树选择器，统一搜索、限高和抽屉内定位。 */
    userSelectProps() {
      return {
        treeData:this.userTree, showSearch:true, allowClear:true, treeNodeFilterProp:'title',
        searchPlaceholder:'输入姓名或部门搜索', dropdownStyle:{maxHeight:'280px',overflow:'auto'},
        getPopupContainer:trigger => trigger.closest('.manual-check-drawer') || document.body,
        notFoundContent:this.userLoading ? '正在加载人员…' : this.userError ? '人员加载失败，请重试' : '未找到匹配人员'
      }
    },
    /** 识别历史记录，保留原有缺失信息。 */
    legacy() { return this.model.flowVersion === FLOW_VERSION.LEGACY },
    /** 识别已完成记录。 */
    completed() { return this.model.flowStatus === FLOW_STATUS.COMPLETED },
    /** 完整录入和完成后修正均提交三个阶段。 */
    fullMode() { return this.completed || (!this.model.id && this.entryMode === STAGE.COMPLETE) },
    /** 详情与完整录入展示全部阶段。 */
    showAll() { return this.fullMode || this.disableSubmit },
    /** 确定当前导航阶段。 */
    focusedStage() { return this.showAll ? this.overviewStage : this.model.id ? this.activeStage : STAGE.SAMPLE },
    /** 展示统一的流程状态名称。 */
    flowLabel() { return FLOW_LABELS[this.model.flowStatus] || '待采样' },
    /** 统计已填写项目，零值计入有效填写。 */
    filledFactorCount() { return this.paramData.filter(item => hasValue(item.value)).length },
    /** 提示当前操作的保存范围。 */
    footerHint() {
      if(this.disableSubmit) return '查看三阶段信息与对应附件'
      if(!syncsMonitoring(this.model.st, this.model.samplingPoint)) return '进水记录仅保留人工检测台账，不进入监测数据'
      if(this.completed) return '保存修正后，将更新对应监测数据'
      if(this.fullMode || this.focusedStage === STAGE.COMPLETE) return '三个阶段完整后，检测结果同步至监测数据'
      return '仅保存当前阶段，后续可继续填写'
    },
    /** 按阶段展示保存按钮名称。 */
    submitLabel() {
      if (this.completed) return '保存修正'
      const stage = this.model.id ? this.activeStage : this.entryMode
      return SUBMIT_LABELS[stage]
    }
  },
  /** 加载人员和实验室选择项。 */
  created() {
    this.loadUsers()
    getAction('/ea/company/info/queryCompanyName').then(res => { if(res.success) this.companyList = res.result })
  },
  /** 退出页面时取消延迟判定并作废旧响应。 */
  beforeDestroy() { clearTimeout(this.qualificationTimer); this.qualificationRequest++ },
  methods: {
    /** 复用原人员接口，提供加载状态及失败重试。 */
    async loadUsers() {
      if(this.userLoading) return
      this.userLoading = true
      this.userError = ''
      try {
        const response = await queryUserTreeList()
        if(!response.success) throw new Error(response.message)
        this.userTree = this.normalizeUserTree(response.result || [])
      } catch(error) { this.userError = error.message || '人员加载失败' }
      finally { this.userLoading = false }
    },
    /** 仅为新版未完成阶段补充时间和当前人员。 */
    stageDefaults(stage) {
      if(this.disableSubmit || this.legacy || this.completed) return
      const dateKey = stage === STAGE.ARRIVAL ? 'arrivalDate' : 'checkDate'
      const userKey = stage === STAGE.ARRIVAL ? 'arrivalUser' : 'checkUser'
      if(!this.model[dateKey]) this.$set(this.model,dateKey,moment())
      if(!this.model[userKey]) this.$set(this.model,userKey,String(this.$store.getters.userInfo.id))
      if(stage === STAGE.COMPLETE && (this.model.checkType == null || this.model.checkType === '')) this.$set(this.model,'checkType',CHECK_TYPE.SELF)
    },
    /** 展示服务端环境标准判定结果。 */
    qualificationText(row) {
      if(row.value == null || row.value === '') return '未填写'
      if(this.qualificationLoading) return '判定中'
      if(this.qualificationError) return '判定失败'
      return QUALIFICATION_LABELS[row.qualificationStatus] || '未配置限值'
    },
    /** 区分合格、异常与待填写状态。 */
    qualificationTone(row) {
      const text = this.qualificationText(row)
      return text === '合格' ? 'pass' : ['不合格','数值无效','判定失败'].includes(text) ? 'fail' : 'pending'
    },
    /** 合并连续输入，减少限值判定请求。 */
    queueQualification() {
      clearTimeout(this.qualificationTimer)
      this.qualificationRequest++
      this.qualificationLoading = true
      this.qualificationTimer = setTimeout(() => this.evaluateFactors(),300)
    },
    /** 复用服务端限值判定，并忽略已过期响应。 */
    async evaluateFactors() {
      const request = ++this.qualificationRequest
      const session = this.sessionKey
      const siteId = this.model.siteId
      const point = this.model.samplingPoint
      const cp = buildCp(this.paramData)
      this.qualificationError = ''
      if(!Object.keys(cp).length) { this.qualificationLoading = false; return }
      this.qualificationLoading = true
      try {
        const response = await httpAction(this.pagePath+'/evaluate',{siteId,samplingPoint:point,cp},'post')
        if(request !== this.qualificationRequest || session !== this.sessionKey || point !== this.model.samplingPoint || siteId !== this.model.siteId) return
        if(!response.success) throw new Error(response.message)
        const rows = new Map(response.result.map(row => [row.code,row]))
        this.paramData = this.paramData.map(row => Object.assign({},row,rows.get(row.code) || {emissionLimit:null,qualificationStatus:null}))
      } catch(error) {
        if(request === this.qualificationRequest && session === this.sessionKey) this.qualificationError = error.message || '限值判定失败，请重试'
      } finally { if(request === this.qualificationRequest && session === this.sessionKey) this.qualificationLoading = false }
    },
    /** 统一人员选择控件的值类型。 */
    normalizeUserTree(nodes) {
      // 人员选择控件使用字符串值，提交时还原数值 ID。
      return nodes.map(node => Object.assign({}, node, {
        value: String(node.value == null ? node.key : node.value),
        children: node.children ? this.normalizeUserTree(node.children) : undefined
      }))
    },
    /** 解析人员名称，缺失时展示占位符。 */
    userLabel(field) {
      if(this.model[field+'_userName']) return this.model[field+'_userName']
      const find = nodes => {
        for(const node of nodes) {
          if(String(node.value) === String(this.model[field])) return node.title
          const name = node.children && find(node.children)
          if(name) return name
        }
      }
      return find(this.userTree) || '—'
    },
    /** 统一详情时间格式。 */
    formatDate(value) { return value ? moment(value).format(DATE_TIME_FORMAT) : '—' },
    /** 按录入模式确定阶段是否展示。 */
    showSection(stage) { return this.showAll || this.focusedStage === stage },
    /** 只开放已提交或当前可填写的阶段。 */
    stageAvailable(stage) {
      if(this.showAll || stage === STAGE.SAMPLE) return true
      if(!this.model.id) return false
      return stage === STAGE.ARRIVAL || this.model.flowStatus === FLOW_STATUS.WAIT_TEST
    },
    /** 标记已提交的阶段。 */
    stageDone(stage) {
      if(stage === STAGE.SAMPLE) return !!this.model.id
      if(stage === STAGE.ARRIVAL) return !!this.model.arrivalDate && (this.model.flowStatus === FLOW_STATUS.WAIT_TEST || this.completed)
      return this.completed
    },
    /** 提示各阶段完成情况。 */
    stageDescription(stage) {
      if(this.stageDone(stage)) return this.disableSubmit ? '已提交' : '已提交，可修正'
      if(this.legacy && stage === STAGE.ARRIVAL) return '历史记录未填'
      if(!this.stageAvailable(stage)) return stage === STAGE.ARRIVAL ? '采样后填写' : '到样后填写'
      return this.disableSubmit ? '尚未填写' : '待填写'
    },
    /** 切换当前阶段或定位完整表单中的对应区域。 */
    selectStage(stage) {
      if(!this.stageAvailable(stage)) return
      this.submitError = ''
      if(this.showAll) {
        this.overviewStage = stage
        this.$nextTick(() => {
          const section = this.$refs[({SAMPLE:'sampleSection',ARRIVAL:'arrivalSection',COMPLETE:'testingSection'})[stage]]
          if(section) section.scrollIntoView({behavior:'smooth',block:'start'})
        })
      } else { if(stage !== STAGE.SAMPLE) this.stageDefaults(stage); this.activeStage = stage }
    },
    /** 控制当前阶段的编辑状态。 */
    editable(stage) { return !this.disableSubmit && !this.detailLoading && (this.fullMode || (this.model.id ? this.activeStage : this.entryMode) === stage) },
    /** 以所选站点打开新增表单。 */
    add(siteId) { if(!siteId) return this.$message.warning('请选择单个站点'); this.edit({siteId}) },
    /** 读取最新详情，避免使用列表中的旧版本数据。 */
    async edit(record) {
      this.sessionKey++
      const session = this.sessionKey
      this.visible = true
      this.detailLoading = true
      this.paramData = []
      this.factorError = ''
      clearTimeout(this.qualificationTimer)
      this.qualificationRequest++
      this.qualificationLoading = false
      this.qualificationError = ''
      this.entryMode = STAGE.SAMPLE
      this.overviewStage = STAGE.SAMPLE
      this.submitError = ''
      this.model = Object.assign({}, record)
      try {
        let data = record
        if(record.id) {
          const response = await getAction(this.pagePath + '/' + record.id)
          if(!response.success) throw new Error(response.message)
          data = response.result
        } else {
          const response = await getAction('/site/info/' + record.siteId)
          if(!response.success) throw new Error(response.message)
          const site = response.result
          const selectable = hasSamplingPoints(site.st)
          data = {siteId:record.siteId, siteName:site.name, st:site.st, samplingPointSelectable:selectable, samplingPoint:selectable ? undefined : SAMPLING_POINT.DEFAULT,
            samplingDate:moment().startOf('hour'), samplingUser:String(this.$store.getters.userInfo.id),
            arrivalDate:moment(), arrivalUser:String(this.$store.getters.userInfo.id), checkDate:moment(),checkUser:String(this.$store.getters.userInfo.id),flowVersion:FLOW_VERSION.THREE_STAGE,checkType:CHECK_TYPE.SELF,cp:{}}
        }
        if(session !== this.sessionKey) return
        if(!record.id) data.maintenanceTaskId = this.model.maintenanceTaskId
        this.model = Object.assign({}, data, {samplingPointSelectable: !!(data.samplingPointSelectable || data.sewage)})
        if(this.model.checkType != null) this.model.checkType = String(this.model.checkType)
        ;['samplingDate','arrivalDate','checkDate'].forEach(key => { this.model[key] = data[key] ? moment(data[key]) : null })
        ;['samplingUser','arrivalUser','checkUser'].forEach(key => { this.model[key] = data[key] == null || data[key] === '' ? undefined : String(data[key]) })
        this.activeStage = data.flowStatus === FLOW_STATUS.WAIT_TEST ? STAGE.COMPLETE : STAGE.ARRIVAL
        if(record.id && !this.completed) this.stageDefaults(this.activeStage)
        this.overviewStage = STAGE.SAMPLE
        this.$nextTick(() => { if(this.$refs.scrollArea) this.$refs.scrollArea.scrollTop = 0 })
        await this.loadSiteFactor(data.cp || {})
      } catch(error) {
        if(session === this.sessionKey) { this.$message.error(error.message || '读取记录失败'); this.close() }
      } finally { if(session === this.sessionKey) this.detailLoading = false }
    },
    /** 切换采样点时重新加载对应因子。 */
    changePoint() { this.model.cp = {}; this.loadSiteFactor({}) },
    /** 加载对应采样点项目，并保留历史记录中的原始因子。 */
    async loadSiteFactor(cp) {
      const session = this.sessionKey
      const point = this.model.samplingPoint
      this.paramData = []
      this.factorError = ''
      clearTimeout(this.qualificationTimer)
      this.qualificationRequest++
      this.qualificationLoading = false
      this.qualificationError = ''
      this.factorLoading = false
      if(this.model.samplingPointSelectable && !point) return
      this.factorLoading = true
      try {
        const response = await getAction(this.pagePath + '/factors', {siteId:this.model.siteId, samplingPoint:point})
        if(!response.success) throw new Error(response.message)
        if(session === this.sessionKey && point === this.model.samplingPoint) {
          this.paramData = response.result.map(item => Object.assign({},item,{value:cp[item.code] == null ? null : Number(cp[item.code])}))
          // 历史记录保留原检测项目，即使站点配置已变化。
          if(this.legacy) Object.keys(cp).forEach(code => {
            if(!this.paramData.some(item=>item.code===code)) this.paramData.push({code,title:code,unitName:'',value:Number(cp[code])})
          })
          if(this.filledFactorCount) await this.evaluateFactors()
        }
      } catch(error) { if(session === this.sessionKey && point === this.model.samplingPoint) this.factorError = error.message || '读取因子失败' }
      finally { if(session === this.sessionKey && point === this.model.samplingPoint) this.factorLoading = false }
    },
    /** 收集当前阶段的附件关联和移除信息。 */
    uploadPayload(refName, prefix, payload) {
      const files = this.$refs[refName].getUploadListToFromData({})
      payload[prefix+'FileList'] = files.sysFileList
      payload[prefix+'FileDelList'] = files.fileDelList
    },
    /** 提交前校验当前阶段必填项、时间顺序及检测值。 */
    validateForm(stage) {
      const fields = []
      if(this.fullMode || stage === STAGE.SAMPLE) {
        fields.push(['siteId','站点'],['samplingDate','采样时间'],['samplingUser','采样人员'])
        if(this.model.samplingPointSelectable) fields.push(['samplingPoint','采样点'])
      }
      if(!this.legacy && (this.fullMode || stage === STAGE.ARRIVAL)) fields.push(['arrivalDate','到样时间'],['arrivalUser','到样人员'])
      if(this.fullMode || stage === STAGE.COMPLETE) {
        fields.push(['checkDate','检测时间'],['checkType','检测方式'])
        if(!this.legacy) fields.push(['checkUser','检测人员'])
        if(this.model.checkType === CHECK_TYPE.THIRD_PARTY) fields.push(['companyId','实验室'])
      }
      const missing = fields.find(([key]) => this.model[key] == null || this.model[key] === '')
      if(missing) return '请填写' + missing[1]
      if((this.fullMode || stage === STAGE.ARRIVAL || stage === STAGE.COMPLETE || this.model.flowStatus === FLOW_STATUS.WAIT_TEST) && !this.legacy && this.model.arrivalDate && this.model.samplingDate && moment(this.model.arrivalDate).isBefore(this.model.samplingDate)) return '到样时间不能早于采样时间'
      const previousTime = this.legacy ? this.model.samplingDate : this.model.arrivalDate
      if((this.fullMode || stage === STAGE.COMPLETE) && this.model.checkDate && previousTime && moment(this.model.checkDate).isBefore(previousTime)) return '检测时间不能早于采样或到样时间'
      if(this.fullMode || stage === STAGE.COMPLETE) {
        const invalid = this.paramData.find(item => item.value != null && item.value !== '' && (!String(item.value).trim() || !Number.isFinite(Number(item.value)) || Number(item.value) < 0))
        if(invalid) return (invalid.title || invalid.code) + '的检测值必须为有效非负数'
      }
      return ''
    },
    /** 仅提交当前阶段字段，避免覆盖其他阶段数据。 */
    async handleOk() {
      if(this.confirmLoading || this.detailLoading) return
      this.submitError = ''
      const stage = this.completed ? STAGE.COMPLETE : this.model.id ? this.activeStage : this.entryMode
      const error = this.validateForm(stage)
      if(error) { this.submitError = error; return this.$message.warning(error) }
      const payload = {id:this.model.id, version:this.model.version}
      const fields = getStageFields(stage, this.fullMode)
      fields.forEach(key => {
        const value = this.model[key]
        payload[key] = /^(sampling|arrival|check)User$/.test(key) && value != null && value !== '' ? Number(value) :
          moment.isMoment(value) ? value.format(DATE_TIME_FORMAT) : value
      })
      if(this.fullMode || stage === STAGE.SAMPLE) this.uploadPayload('samplingUpload','sampling',payload)
      if(this.fullMode || stage === STAGE.ARRIVAL) this.uploadPayload('arrivalUpload','arrival',payload)
      if(this.fullMode || stage === STAGE.COMPLETE) {
        if(this.$refs.testingUpload.uploading) return this.$message.warning('请等待检测附件上传完成')
        this.uploadPayload('testingUpload','testing',payload)
        if(this.factorError) return this.$message.warning(this.factorError)
        payload.cp = buildCp(this.paramData)
        if(!Object.keys(payload.cp).length) return this.$message.warning('请填写至少一个检测因子')
      }
      let url = this.pagePath
      if(!this.model.id) { payload.submitStage = stage; payload.maintenanceTaskId = this.model.maintenanceTaskId }
      else if(!this.completed) url += '/' + this.model.id + '/' + STAGE_PATHS[stage]
      this.confirmLoading = true
      try {
        const response = await httpAction(url,payload,this.model.id ? 'put' : 'post')
        if(!response.success) {
          this.submitError = response.message || '请检查填写内容后重试'
          this.$notification.warning({message:this.submitError})
          return
        }
        this.$notification.success({message:response.message})
        this.$emit('ok')
        this.close()
      } catch(error) { this.submitError = '保存失败，请检查网络后重试'; this.$message.error(this.submitError) }
      finally { this.confirmLoading = false }
    }
  }
}
</script>

<style lang="less">
.manual-check-drawer {
  --check-bg: #102a52;
  --check-panel: #183860;
  --check-soft: #122e53;
  --check-text: #eef4ff;
  --check-muted: #b2c5e0;
  --check-border: #385579;
  --check-accent: #75baff;
  --check-selected: #214b7c;
  color: var(--check-text);
  &.manual-check-light {
    --check-bg: #f4f7fb;
    --check-panel: #ffffff;
    --check-soft: #f7f9fc;
    --check-text: #24354b;
    --check-muted: #617289;
    --check-border: #dce4ef;
    --check-accent: #1677d2;
    --check-selected: #eaf3ff;
    .flow-badge.finished { color:#168456; }
    .done:not(.selected) .stage-number { color:#168456; border-color:#168456; }
  }
  .ant-drawer-wrapper-body { display:flex; flex-direction:column; background:var(--check-bg) !important; }
  .ant-drawer-header { flex:none; padding:18px 28px; border-bottom:1px solid var(--check-border); background:var(--check-panel) !important; }
  .ant-drawer-title, .ant-drawer-close { color:var(--check-text) !important; }
  .ant-drawer-body { flex:1; min-height:0; padding:0; overflow:hidden; display:flex; flex-direction:column; }
  .ant-form-item-label > label, .ant-form-item-control > span { color:var(--check-text) !important; }
  .ant-form-item { margin-bottom:20px; }
  .ant-form-item-label { padding-bottom:7px; line-height:22px; }
  .ant-select, .ant-calendar-picker, .ant-input-number { width:100% !important; }
  .ant-input, .ant-select-selection, .ant-input-number { border-radius:6px; color:#24354b; }
  .ant-calendar-picker-input, .ant-input-number-input, .ant-select-selection--single { height:38px; }
  .ant-select-selection__rendered { line-height:36px; }
  .ant-input:not(textarea) { min-height:38px; }
  .ant-radio-button-wrapper { height:36px; line-height:34px; }
  .method-switch .ant-radio-button-wrapper { min-width:108px; text-align:center; background:var(--check-soft); border-color:var(--check-border); color:var(--check-muted); }
  .method-switch .ant-radio-button-wrapper::before { background:var(--check-border); }
  .method-switch .ant-radio-button-wrapper .anticon { visibility:hidden; }
  .method-switch .ant-radio-button-wrapper:hover { color:var(--check-accent); }
  .method-switch .ant-radio-button-wrapper-checked { background:#1890ff; border-color:#1890ff; color:#fff; font-weight:600; box-shadow:0 0 0 1px #1890ff; }
  .method-switch .ant-radio-button-wrapper-checked:hover { background:#1890ff; border-color:#1890ff; color:#fff; }
  .method-switch .ant-radio-button-wrapper-checked::before { background:#1890ff; }
  .method-switch .ant-radio-button-wrapper-checked .anticon { visibility:visible; }
  .ant-table { color:var(--check-text); }
  .ant-table-thead > tr > th { background:var(--check-soft); color:var(--check-muted); border-bottom:1px solid var(--check-border); }
  .ant-table-tbody > tr > td { border-bottom:1px solid var(--check-border); }
  .ant-table-tbody > tr:hover > td { background:var(--check-selected); }
  .ant-table-placeholder { background:var(--check-panel); color:var(--check-muted); border-color:var(--check-border); }
  .ant-empty-description { color:var(--check-muted); }
  .ant-table a { color:var(--check-accent); }
.check-scroll { flex:1; min-height:0; overflow:auto; padding:16px 28px; }
.check-overview { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; flex-wrap:wrap; }
.site-heading { display:flex; align-items:center; gap:8px; min-width:0; flex:1; }
.site-heading > .anticon { color:var(--check-accent); font-size:18px; }
.site-heading h2 { margin:0; color:var(--check-text); font-size:16px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.overview-actions { display:flex; align-items:center; gap:12px; flex:none; }
.overview-actions .ant-radio-button-wrapper { height:30px; line-height:28px; }
.flow-badge { flex:none; padding:5px 12px; border-radius:20px; background:var(--check-selected); color:var(--check-accent); font-size:12px; }
.flow-badge.finished { color:#53cda0; background:rgba(44,174,125,.13); }
.field-hint { color:var(--check-muted); font-size:12px; }
.stage-nav { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; margin-bottom:14px; position:sticky; top:0; z-index:4; background:var(--check-bg); box-shadow:0 0 0 8px var(--check-bg); }
.stage-nav button { display:flex; align-items:center; gap:8px; padding:8px 12px; background:var(--check-panel); border:1px solid var(--check-border); border-radius:6px; color:var(--check-text); text-align:left; cursor:pointer; transition:background .2s,border-color .2s; }
.stage-nav button.selected { border-color:var(--check-accent); background:var(--check-selected); }
.stage-nav button:disabled { cursor:not-allowed; color:var(--check-muted); }
.stage-nav button:focus-visible { outline:2px solid var(--check-accent); outline-offset:3px; }
.stage-number { display:flex; align-items:center; justify-content:center; width:22px; height:22px; flex:none; border:1px solid var(--check-border); border-radius:50%; font-weight:600; font-size:12px; }
.selected .stage-number { color:#fff; border-color:#1890ff; background:#1890ff; }
.done:not(.selected) .stage-number { color:#53cda0; border-color:#53cda0; }
.stage-caption { flex:1; display:flex; align-items:center; gap:8px; min-width:0; }
.stage-caption strong { font-size:14px; flex:none; }
.stage-caption small { color:var(--check-muted); font-size:12px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.previous-summary { display:flex; flex-wrap:wrap; gap:8px 24px; padding:0 2px 16px; color:var(--check-muted); font-size:12px; }
.stage-card { padding:18px 22px 4px; margin-bottom:16px; border:1px solid var(--check-border); border-radius:8px; background:var(--check-panel); scroll-margin-top:56px; }
.section-heading { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding-bottom:18px; margin-bottom:20px; border-bottom:1px solid var(--check-border); }
.section-heading h3 { margin:0; color:var(--check-text); font-size:16px; font-weight:600; }
.section-heading h3 span { color:var(--check-accent); margin-right:10px; font-size:13px; }
.section-heading p { color:var(--check-muted); margin:6px 0 0; font-size:12px; }
.section-state { flex:none; color:var(--check-muted); font-size:12px; padding-top:2px; }
.fields-row { display:flex; flex-wrap:wrap; }
.field-value { min-height:38px; padding:7px 0; color:var(--check-text); line-height:24px; word-break:break-word; }
.readonly-form .ant-form-item { margin-bottom:12px; }
.readonly-form .ant-form-item-label { padding-bottom:4px; }
.readonly-form .ant-form-item-control { line-height:28px; }
.readonly-form .field-value { min-height:28px; padding:0; line-height:28px; }
.readonly-form .stage-card { padding-bottom:16px; }
.readonly-form .section-heading { padding-bottom:12px; margin-bottom:16px; }
.readonly-form .section-state { display:none; }
.readonly-form .fields-row { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); margin:0 0 16px !important; border:1px solid var(--check-border); border-radius:6px; overflow:hidden; background:var(--check-soft); }
.readonly-form .fields-row::before, .readonly-form .fields-row::after,
.readonly-form .fields-row .ant-form-item::before, .readonly-form .fields-row .ant-form-item::after { display:none; }
.readonly-form .fields-row > div { width:auto; padding:0 !important; border-bottom:1px solid var(--check-border); }
.readonly-form .fields-row > div:nth-child(odd) { border-right:1px solid var(--check-border); }
/* 保留网格起止线写法，避免 Less 将斜杠当成除法。 */
.readonly-form .fields-row > div:last-child:nth-child(odd) { grid-column:~"1 / -1"; border-right:0; }
.readonly-form .fields-row > div:last-child, .readonly-form .fields-row > div:nth-last-child(2):nth-child(odd) { border-bottom:0; }
.readonly-form .fields-row .ant-form-item { display:grid; grid-template-columns:76px minmax(0,1fr); align-items:start; gap:12px; padding:14px 16px; margin:0; }
.readonly-form .ant-form-item-label > label { color:var(--check-muted) !important; font-size:12px; font-weight:400; }
.readonly-form .fields-row .ant-form-item-label { padding:0; line-height:26px; }
.readonly-form .fields-row .field-value { min-height:26px; line-height:26px; font-size:15px; font-weight:600; font-variant-numeric:tabular-nums; }
.readonly-form .remarks-field { margin:0 0 16px; padding:12px 16px; background:var(--check-soft); border-left:3px solid var(--check-border); border-radius:4px; }
.readonly-form .remarks-field .field-value { font-size:14px; font-weight:400; white-space:pre-wrap; }
.readonly-form .remarks-field .empty-value { color:var(--check-muted); font-size:12px; }
.readonly-form .testing-remarks { margin-top:16px; }
.readonly-form .attachment-box { padding:12px 16px; border-style:solid; }
.readonly-form .attachment-box .upload-class { margin-bottom:0 !important; }
.readonly-form .attachment-box .field-hint { margin:0; }
.readonly-form .stage-card > .ant-form-item:last-child { margin-bottom:0; }
.attachment-box { padding:14px 16px 4px; border:1px dashed var(--check-border); border-radius:8px; background:var(--check-soft); }
.attachment-box .ant-table-wrapper { overflow-x:auto; }
.attachment-box .ant-table { min-width:540px; background:var(--check-panel); border-radius:6px; overflow:hidden; }
.attachment-box .ant-table-bordered .ant-table-body > table,
.attachment-box .ant-table-bordered .ant-table-thead > tr > th,
.attachment-box .ant-table-bordered .ant-table-tbody > tr > td,
.attachment-box .ant-table-bordered .ant-table-placeholder { border-color:var(--check-border); }
.field-hint { margin:0 0 12px; line-height:20px; display:block; }
.result-heading { display:flex; justify-content:space-between; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
.result-heading strong { color:var(--check-text); font-weight:500; }
.result-heading > span { color:var(--check-muted); font-size:12px; }
.required-mark { color:#ff7779; }
.factor-table { border:1px solid var(--check-border); border-radius:8px; overflow:hidden; }
.factor-table .ant-table { background:var(--check-panel); }
.factor-table .ant-table-body { overflow-x:auto !important; background:var(--check-panel); }
.factor-table .ant-table-body, .attachment-box .ant-table-wrapper { scrollbar-width:thin; scrollbar-color:var(--check-border) var(--check-soft); }
.factor-table .ant-table-body::-webkit-scrollbar, .attachment-box .ant-table-wrapper::-webkit-scrollbar { width:6px; height:6px; }
.factor-table .ant-table-body::-webkit-scrollbar-track, .attachment-box .ant-table-wrapper::-webkit-scrollbar-track { background:var(--check-soft); }
.factor-table .ant-table-body::-webkit-scrollbar-thumb, .attachment-box .ant-table-wrapper::-webkit-scrollbar-thumb { background:var(--check-border); border-radius:3px; }
.factor-table .ant-table-tbody > tr > td { background:var(--check-panel); color:var(--check-text); padding:14px 16px; }
.factor-table .ant-table-tbody > tr:hover > td { background:var(--check-selected); }
.factor-name { color:var(--check-text) !important; font-size:15px; font-weight:600; }
.factor-unit { color:var(--check-muted) !important; font-size:14px; }
.result-number { color:var(--check-accent) !important; font-size:18px; font-weight:600; font-variant-numeric:tabular-nums; }
.qualification-pill { display:inline-block; padding:3px 8px; border-radius:4px; font-size:12px; white-space:nowrap; }
.qualification-pill.pass { color:#39cb90; background:rgba(42,178,116,.13); }
.qualification-pill.fail { color:#ff8b8b; background:rgba(232,82,82,.13); }
.qualification-pill.pending { color:var(--check-muted); background:var(--check-soft); }
&.manual-check-light .qualification-pill.pass { color:#167b4c; }
&.manual-check-light .qualification-pill.fail { color:#c33535; }
.factor-table .ant-input-number { background:#fff; border-color:#a6c3e6; }
.factor-table .ant-input-number-input { color:#183451; font-size:16px; font-weight:600; }
.testing-attachments { margin-top:20px; }
.testing-remarks { margin-top:18px; }
.factor-notice, .legacy-notice { margin-bottom:16px; }
.standard-summary { color:var(--check-muted); margin-bottom:20px; }
.audit-summary { color:var(--check-muted); font-size:12px; display:flex; flex-wrap:wrap; gap:8px 24px; padding:0 4px 6px; }
.check-footer { flex:none; display:flex; justify-content:space-between; align-items:center; gap:16px; padding:16px 28px; background:var(--check-panel); border-top:1px solid var(--check-border); box-shadow:0 -4px 20px rgba(0,0,0,.06); }
.check-footer p { margin:0; color:var(--check-muted); font-size:12px; }
.check-footer > div { display:flex; gap:10px; flex:none; }
.check-footer .ant-btn { height:38px; min-width:88px; border-radius:6px; }
@media(max-width:600px) {
  .check-scroll { padding:18px 14px; }
  .check-overview { gap:8px; }
  .overview-actions { gap:8px; }
  .stage-card { padding:18px 16px 2px; }
  .stage-nav { gap:6px; }
  .stage-nav button { gap:6px; padding:8px; }
  .stage-caption small { display:none; }
  .stage-nav button > .anticon-right { display:none; }
  .check-footer { padding:12px 16px; }
  .check-footer p { max-width:45%; line-height:18px; }
  .section-state { display:none; }
  .readonly-form .fields-row { grid-template-columns:1fr; }
  .readonly-form .fields-row > div { border-right:0; }
  .readonly-form .fields-row > div:nth-child(odd) { border-right:0; }
  .readonly-form .fields-row > div:nth-last-child(2):nth-child(odd) { border-bottom:1px solid var(--check-border); }
  .readonly-form .fields-row .ant-form-item { gap:8px; padding:12px; }
}
}
</style>
